import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Inbox, Loader2, MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, get, post } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAsync } from "@/lib/queries";
import { formatDate, formatTime } from "@/lib/constants";
import { StabVestBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "@/components/States";
import { CompanyBar } from "@/components/CompanyBar";

const OpenJobCard = ({ run, onAccept, busy }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`available-job-${run.id}`}>
    <div className="flex items-start justify-between gap-3">
      <p className="font-data text-xs uppercase tracking-wider text-slate-500">{formatDate(run.run_date)}</p>
      {run.stab_vest_required && <StabVestBadge testId={`available-job-stab-vest-${run.id}`} />}
    </div>
    <p className="font-display mt-1 flex items-center gap-2 text-lg font-bold text-slate-900"><Clock className="h-4 w-4 text-blue-700" />Start {formatTime(run.start_time)}</p>
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
      <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />{run.officer?.full_name || "Officer"}</span>
      <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{run.address_count} address{run.address_count === 1 ? "" : "es"}</span>
    </div>
    {run.postcodes.length > 0 && <p className="mt-2 font-data text-sm text-slate-700" data-testid={`available-job-postcodes-${run.id}`}>{run.postcodes.join(", ")}</p>}
    <Button className="mt-4 min-h-[44px] w-full bg-blue-700 hover:bg-blue-800" disabled={busy} onClick={() => onAccept(run)} data-testid={`available-job-accept-${run.id}`}>
      {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Accept job
    </Button>
  </div>
);

export default function AvailableJobs() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);
  const ready = !!profile.company?.active;
  const { data, loading, error, reload } = useAsync(() => (ready ? get("/runs/available") : Promise.resolve(null)), [profile.company_id, ready]);

  const accept = async (run) => {
    setBusyId(run.id);
    try {
      await post(`/runs/${run.id}/accept`);
      toast.success(profile.role === "manager" ? "Job accepted. It's now assigned to you." : "Job accepted. It's now in My Jobs.");
      navigate(profile.role === "manager" ? `/manager/runs/${run.id}` : `/jobs/${run.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
      reload();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="available-jobs-page">
      <PageHeader eyebrow="Open to your team" title="Available jobs" description="Open runs no one has taken yet. The first person to accept gets the job." />
      <CompanyBar onChanged={reload} />
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && ready && !data && <LoadingRows rows={3} />}
      {data && !data.length && <EmptyState icon={Inbox} title="No available jobs" description="When your manager posts an open run, you'll get an email and it will show here." testId="available-jobs-empty" />}
      <div className="space-y-3">{(data || []).map((r) => <OpenJobCard key={r.id} run={r} onAccept={accept} busy={busyId === r.id} />)}</div>
    </div>
  );
}
